// @flow

import * as React from "react";
import debounce from "lodash/debounce";
import SVG from "react-inlinesvg";

import ModalLauncher from "../ModalLauncher";
import TextInput from "../Input/TextInput";
import type { InputType } from "../Input/TextInput";
import Preloader from "../Preloader";
import CheckmarkIcon from "../../assets/icons/other/checkmark.svg";

import styles from "./EmailInput.scss";

type EmailInputProps = {
  content: ?() => React.Node,
  label: string,
  placeholder: string,
  isEmailAvailable: boolean,
  checkAsyncEmail: ?(value: string) => Promise<any>,
  input: InputType,
  modalHeader?: string,
  valid: boolean,
  history: {
    push: (path: string) => void
  },
  onError: () => void,
  isLocked: ?boolean,
  hasSideSymbol: ?boolean
};

type EmailInputState = {
  isSearching: boolean
};

class EmailInput extends React.Component<EmailInputProps, EmailInputState> {
  static defaultProps = {
    placeholder: "",
    label: "",
    input: null,
    history: {
      push: () => {}
    },
    content: () => null,
    modalHeader: null,
    onError: () => {},
    isLocked: false,
    hasSideSymbol: true
  };

  state = {
    isSearching: false
  };

  unmounted: boolean = false;

  checkEmailAvailabilty = debounce(async (value: string) => {
    if (this.unmounted) {
      return;
    }
    try {
      const { checkAsyncEmail } = this.props;
      if (checkAsyncEmail) {
        await checkAsyncEmail(value);
      }
      this.setSearching(false);
    } catch (e) {
      const { onError } = this.props;

      // eslint-disable-next-line no-console
      console.error(e);
      onError();
    }
  }, 300);

  componentDidUpdate = ({ input }: InputType) => {
    const { value: prevValue } = input;
    const {
      input: { value: newValue }
    } = this.props;

    if (prevValue !== newValue) {
      this.handleInputChange();
    }
  };

  componentWillUnmount() {
    this.unmounted = true;
  }

  handleInputChange = () => {
    const {
      input: { value },
      valid
    } = this.props;

    if (value && valid) {
      this.setSearching(true);
      this.checkEmailAvailabilty(value);
    }
  };

  setSearching = (isSearching: boolean) => this.setState({ isSearching });

  renderSideSymbol = () => {
    const { isSearching } = this.state;
    const { isEmailAvailable, valid } = this.props;

    if (isSearching) {
      return (
        <span data-testid="spinner">
          <Preloader size="small" />
        </span>
      );
    }
    if (isEmailAvailable && valid) {
      return (
        <span data-testid="success">
          <SVG className={styles.checkmarkIcon} src={CheckmarkIcon} />
        </span>
      );
    }

    return "";
  };

  shouldDisplayNote = () => {
    const { isEmailAvailable, valid } = this.props;
    const { isSearching } = this.state;

    return !isEmailAvailable && !isSearching && valid;
  };

  renderNote = () => {
    const { content, modalHeader } = this.props;

    return (
      this.shouldDisplayNote() && (
        <div className={styles.note}>
          Diese Adresse existiert bereits. Haben Sie bereits ein Konto?&nbsp;
          <ModalLauncher
            trigger={({ open }) => (
              <button
                type="button"
                className={styles.button}
                onClick={open}
                onKeyDown={e => e.keyCode === 13 && open()}
              >
                JETZT ANMELDEN
              </button>
            )}
            header={() => modalHeader}
            content={content}
          />
        </div>
      )
    );
  };

  render() {
    const {
      label,
      placeholder,
      input,
      valid: ignoredValid,
      isEmailAvailable: ignoredIsEmailAvailable,
      checkAsyncEmail: ignoredCheckAsyncEmail,
      content: ignoredContent,
      modalHeader: ignoredModalHeader,
      isLocked,
      hasSideSymbol,
      ...restProps
    } = this.props;

    return (
      <React.Fragment>
        <TextInput
          label={label}
          placeholder={placeholder}
          sideSymbol={hasSideSymbol && this.renderSideSymbol}
          type="email"
          input={input}
          data-error={this.shouldDisplayNote()}
          isLocked={isLocked}
          {...restProps}
        />
        {this.renderNote()}
      </React.Fragment>
    );
  }
}

export default EmailInput;
